import React, { ReactNode } from "react";
import { Search, List, Grid, Filter, X, ChevronDown, ChevronUp, LucideIcon } from "lucide-react";
import { ViewToggle } from "./ViewToggle";

export interface FilterConfig {
  label: string;
  value: string;
  onClear: () => void;
}

export interface ActionButton {
  label: string;
  onClick: () => void;
  icon?: LucideIcon;
  variant?: "primary" | "secondary";
  disabled?: boolean;
  loading?: boolean;
}

interface RecordsPageHeaderProps {
  recordCount?: number;
  recordLabel?: string;
  recordLabelPlural?: string;
  
  // Search
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  
  // View Mode
  viewMode?: "list" | "grid";
  onViewModeChange?: (mode: "list" | "grid") => void;
  
  // Filters
  showFilters?: boolean;
  filtersOpen?: boolean;
  onToggleFilters?: () => void;
  activeFilterCount?: number;
  activeFilters?: FilterConfig[];
  filtersContent?: ReactNode;
  
  // Actions
  primaryAction?: ActionButton;
  secondaryActions?: ActionButton[];
  
  // Bulk Actions
  showBulkActions?: boolean;
  selectedCount?: number;
  bulkActions?: ReactNode;
  
  // Additional content
  children?: ReactNode;
}

export const RecordsPageHeader: React.FC<RecordsPageHeaderProps> = ({
  recordCount,
  recordLabel = "record",
  recordLabelPlural = "records",
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  viewMode,
  onViewModeChange,
  showFilters = false,
  filtersOpen = false,
  onToggleFilters,
  activeFilterCount = 0,
  activeFilters = [],
  filtersContent,
  primaryAction,
  secondaryActions = [],
  showBulkActions = false,
  selectedCount = 0,
  bulkActions,
  children,
}) => {
  return (
    <div className="space-y-4">
      {/* Search, View Toggle, Filters, and Actions Row */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Search Bar */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-shadow"
          />
        </div>

        {/* View Mode Toggle */}
        {viewMode && onViewModeChange && (
          <ViewToggle
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
            options={[
              { value: 'list', icon: List, title: 'List view' },
              { value: 'grid', icon: Grid, title: 'Grid view' }
            ]}
            iconSize={16}
          />
        )}

        {/* Filters Button */}
        {showFilters && onToggleFilters && (
          <div className="relative">
            <button
              onClick={onToggleFilters}
              data-filters-toggle-button
              className={`px-3 py-2 text-sm border rounded-lg flex items-center gap-1.5 transition-colors ${
                filtersOpen || activeFilterCount > 0
                  ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300"
                  : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
              }`}
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-primary-600 text-white text-xs rounded-full">
                  {activeFilterCount}
                </span>
              )}
              {filtersOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {filtersContent}
          </div>
        )}

        {/* Secondary Actions */}
        {secondaryActions.map((action, index) => {
          const Icon = action.icon;
          return (
            <button
              key={index}
              onClick={action.onClick}
              disabled={action.disabled}
              className={`flex items-center space-x-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                action.variant === "primary"
                  ? "bg-accent-600 text-white hover:bg-accent-700"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {Icon && <Icon className={`w-4 h-4 ${action.loading ? "animate-spin" : ""}`} />}
              <span>{action.label}</span>
            </button>
          );
        })}

        {/* Primary Action */}
        {primaryAction && (
          <button
            onClick={primaryAction.onClick}
            disabled={primaryAction.disabled}
            className={`flex items-center space-x-2 px-3 py-2 text-sm rounded-lg transition-colors ${
              primaryAction.variant === "secondary"
                ? "bg-primary-600 text-white hover:bg-primary-700"
                : "bg-orange-600 text-white hover:bg-orange-700"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {primaryAction.icon && (
              <primaryAction.icon
                className={`w-4 h-4 ${primaryAction.loading ? "animate-spin" : ""}`}
              />
            )}
            <span>{primaryAction.label}</span>
          </button>
        )}
      </div>

      {/* Record Count and Active Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {recordCount !== undefined && (
          <span className="text-xs text-gray-600 dark:text-gray-400">
            {recordCount} {recordCount === 1 ? recordLabel : recordLabelPlural}
          </span>
        )}
        
        {/* Active Filter Chips */}
        {activeFilters.length > 0 && (
          <>
            <span className="text-xs text-gray-400">•</span>
            <div className="flex items-center gap-1.5 flex-wrap">
              {activeFilters.map((filter, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded text-xs"
                >
                  {filter.label}
                  <button
                    onClick={filter.onClear}
                    className="hover:bg-primary-200 dark:hover:bg-primary-800 rounded-full p-0.5 transition-colors"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {showBulkActions && selectedCount > 0 && (
        <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-primary-900 dark:text-primary-100">
              {selectedCount} selected
            </span>
            {bulkActions}
          </div>
        </div>
      )}

      {/* Additional Content */}
      {children}
    </div>
  );
};
